import Decimal from 'decimal.js'
import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'

import { makeStyles } from '@mui/styles'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import lang from '@/lang'

import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import PositionWithTaxCategory from './_helpers/PositionWithTaxCategory'
import { priceKinds, expenseStates } from '@/modules/base/util/constants'
import AddIconSvg from '@assets/images/icons/add.svg'
import PriceKindSwitch from './price-kind-switch/price-kind-switch'
// FLAG:useDocuAi — click-to-edit for the document's own totals (same interaction as the line description).
import InlineAmountEdit from './inline-amount-edit'
// FLAG:useDocuAi — the receipt contains multiple VAT rates but none per position: the
// position total is then an assumption and must not be compared with the receipt's stated net.
import { hasAssumedLineTax, isBookedByTaxRate, formatStatedRates } from '@/modules/receipt/_helpers/assumedLineTaxHint'

const { OPEN } = expenseStates

const useStyles = makeStyles(theme => ({
  positionSumRow: props => ({
    minWidth: 'calc(24% + 132px)',
  }),

  switchWrapper: {
    paddingBottom: 10,
  },

  addButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start;',
    height: 54,
    borderRadius: 4,
    cursor: 'pointer',
    flexDirection: 'row',
    paddingLeft: '13px',
    paddingBottom: '9px',
  },

  addButtonIconWrapper: {
    display: 'flex',
    padding: 5,
    background: theme.primary,
    borderRadius: '50%',
  },

  addButtonLabel: {
    fontSize: 14,
    fontWeight: 500,
    marginLeft: 10,
    color: 'var(--color-primary)',
  },
  container: {
    flexDirection: 'row',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderTop: theme.border,
    paddingTop: 20,
    marginTop: 20,
  },
  containerReverse: {
    flexDirection: 'row-reverse',
  },
  // FLAG:useDocuAi — the "these numbers do not add up" advice under the stated totals. Yellow
  // review color, never red: a mismatch is something to look at, not a failure — a discount, a
  // rounding line or a misread position can all cause it, and only the reviewer can tell.
  mismatch: {
    marginTop: 8,
    padding: '6px 8px',
    borderRadius: 4,
    border: `1px solid ${theme.colors?.yellow || theme.yellow}`,
    fontSize: theme.fontSizes.smaller,
    color: theme.textColor,
    lineHeight: 1.35,
  },
}))

const useStylesRow = makeStyles(theme => ({
  row: props => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: props.bold ? theme.text : theme.textLight,
    fontWeight: props.bold ? 500 : 'inherit',
  }),
  amount: {
    textAlign: 'right',
  },

}))

const PositionWithTaxCategorySumRow = (props) => {
  const { label, value } = props
  const classes = useStylesRow(props)

  return (
    <li className={classes.row}>
      <span>{label}</span>
      <span className={classes.amount}>{value}</span>
    </li>
  )
}

export const PositionsWithTaxCategorySum = (props) => {
  const classes = useStyles(props)
  const {
    priceKind = priceKinds.GROSS,
    state,
    priceTotal,
    positions,
    dateFor,
    onAddPosition,
    disabled,
    onChangePriceKind,
    hideAddPosition = false,
    // FLAG:useDocuAi — whole-invoice document allowance. positionsTotal = Σ positions
    // (pre-adjustment); documentAllowance is signed (positive = allowance/discount,
    // negative = surcharge), with positionsTotal − documentAllowance = net (priceTotal here).
    // Both are only ever set on the DocuAI review/booked-expense data, so legacy callers
    // (which never pass them) render byte-identical to before.
    positionsTotal,
    documentAllowance,
    // FLAG:useDocuAi — typed document allowances/charges (v1.3.0 BG-20/21): enrich the
    // discount/surcharge label with reason + tax rate. Additive; undefined for legacy
    // callers → the scalar-only label renders exactly as before.
    allowanceCharges,
    // FLAG:useDocuAi — the invoice's OWN totals as DocuAI read them off the document
    // ({ priceNet, vatAmount, totalAmount }). Present ONLY in the invoice review; every legacy
    // caller leaves it undefined and keeps the computed sum block untouched.
    documentTotals,
    // FLAG:useDocuAi — VAT per tax rate as stated by the RECEIPT (EN 16931 BG-23), as
    // `{ '19': -0.92, '7': -0.20 }`. Only XML receipts carry it (contractToAnalysis populates it
    // behind isXmlSource); everywhere else it is undefined ⇒ VAT rows continue to be derived from
    // the line items as before. Necessary because a document allowance reduces the taxable base:
    // the rows then add up to more than the receipt taxes, and net × rate diverges.
    documentVat,
    onChangeDocumentTotal,
    // FLAG:useDocuAi — invoiceFacts.lineTaxAssumed: the document charges several VAT rates but
    // states none per position, so the rate behind every derived per-line net is OUR assumption.
    // Only the DocuAI review ever passes it; undefined everywhere else keeps the hints unchanged.
    assumedLineTax,
    // FLAG:useDocuAi — hide the gross/net switch where the price base is a FACT of the
    // document, not a view the reviewer picks. On an EN 16931 e-invoice the line amounts are
    // net by definition (BT-131) and net/VAT/total are all three stated on the document
    // (BT-109/110/112), so the switch had nothing left to switch: the stated-totals block
    // below ignores priceKind entirely, and a grossed-up line (158,88 → 170,0016) is a figure
    // that appears nowhere on the invoice. Default false ⇒ every legacy caller renders
    // byte-identical to before.
    hidePriceKindSwitch = false,
    // FLAG:useDocuAi — on an XML e-invoice the stated totals are the legally binding values
    // mapped from the document, and the server keeps its own copy on save
    // (api/src/services/fileService/_helpers/xmlLockedFields.ts: totalAmount/priceNet/
    // vatAmount are restored from the analysis). Offering the inline edit therefore let a
    // reviewer type a correction that silently vanished on save. Show, don't edit.
    lockDocumentTotals = false,
    // FLAG:useDocuAi — receipt currency code. Otherwise formatCurrency uses config.currencyFormat
    // and hard-codes „€" under a USD invoice. The default leaves every legacy caller unchanged.
    currencySymbol = '€',
  } = props
  const formatAmount = value => formatCurrency(value, { symbol: currencySymbol })
  const [totalNet, setTotalNet] = useState(0)
  const [totalGross, setTotalGross] = useState(0)
  const [vat, setVat] = useState({})

  useEffect(() => {
    const result = positions.reduce((calculated, position) => {
      const {
        totalGross: positionGross,
        totalNet: positionNet,
        vat: positionVatAmount,
        vatPercent: positionVatPercent,
      } = position
      const { totalGross, totalNet, vat } = calculated
      let actualVatAmount = vat[positionVatPercent]
      if (!actualVatAmount) {
        actualVatAmount = vat[positionVatPercent] = new Decimal(0)
      }

      return {
        totalGross: totalGross.plus(positionGross),
        totalNet: totalNet.plus(positionNet),
        vat: {
          ...vat,
          [positionVatPercent]: actualVatAmount.plus(positionVatAmount),
        },
      }
    }, {
      totalNet: new Decimal(0),
      totalGross: new Decimal(0),
      vat: {},
    })

    const totalGross = state !== OPEN
      ? priceTotal
      : result.totalGross.toDP(2).toNumber()

    setTotalGross(totalGross)
    setTotalNet(result.totalNet.toDP(2).toNumber())
    setVat(result.vat)
  }, [positions])

  const vatComponents = useMemo(() => {
    const taxes = Object.keys(vat).reverse()
    return taxes.map(taxValue => {
      const value = vat[taxValue].toDP(2).toNumber()
      if (value === 0) return null

      const label = priceKind === priceKinds.NET ? `USt. ${taxValue}%` : `Enth. USt. ${taxValue}%`
      const formattedValue = formatAmount(value)
      const key = `position-${taxValue}`
      return (
        <PositionWithTaxCategorySumRow key={key} label={label} value={formattedValue} />
      )
    })
  }, [vat, priceKind])

  const handleAddPosition = () => {
    const currentTotal = positions.reduce((result, position) => {
      result = result.plus(position.totalGross)
      return result
    }, new Decimal(0))
    const outstandingAmount = Decimal(priceTotal).minus(currentTotal).toDP(2).toNumber()
    const position = new PositionWithTaxCategory({ totalGross: outstandingAmount, dateFor })
    onAddPosition(position)
  }

  // FLAG:useDocuAi — a whole-invoice allowance/discount (or surcharge) makes Σ positions ≠ net.
  // Show it as a header adjustment (line-item total → discount → total), never a tax-relevant
  // position. Falsy/absent documentAllowance ⇒ legacy render, byte-identical.
  const allowance = Number(documentAllowance) || 0
  const hasAllowance = allowance !== 0 && positionsTotal != null
  // Typed detail (reason + rate) for the discount/surcharge label, when DocuAI
  // supplied the BG-20/21 breakdown. Falls back to the scalar-only label.
  const allowanceDetail = Array.isArray(allowanceCharges) && allowanceCharges.length
    ? allowanceCharges
      .map(ac => [ac.reason, ac.taxRate != null ? `USt ${ac.taxRate}%` : null].filter(Boolean).join(' · '))
      .filter(Boolean)
      .join(' | ')
    : null
  // FLAG:useDocuAi — "Allowance or surcharge?" is a question about EFFECT, not the absolute sign: an
  // allowance decreases the amount, while a surcharge increases it. The convention
  // "positive = allowance" (`positionsTotal − documentAllowance = priceNet`) therefore applies only
  // to a POSITIVE receipt. On a credit note/invoice correction, every sign flips too:
  // −7.90 − (−0.23) = −7.67, so the printed „Abschlag Rechnungsrabatt" arrives as −0.23 and used to
  // be labelled "surcharge" here. What matters is the sign RELATIVE to the receipt. On a positive
  // receipt, `documentSign` is 1 ⇒ the expression remains identical to before.
  const documentSign = Number(positionsTotal) < 0 ? -1 : 1
  const allowanceLabel = allowance * documentSign >= 0
    ? lang.taxCategoryPositions.documentAllowance
    : lang.taxCategoryPositions.documentSurcharge
  const allowanceRows = hasAllowance
    ? (
      <>
        <PositionWithTaxCategorySumRow
          label={lang.taxCategoryPositions.positionsTotal}
          value={formatAmount(Number(positionsTotal))} />
        <PositionWithTaxCategorySumRow
          label={allowanceDetail ? `${allowanceLabel} (${allowanceDetail})` : allowanceLabel}
          value={formatAmount(-allowance)} />
      </>)
    : null

  const positionSum = priceKind === priceKinds.NET
    ? (
      <>
        {hasAllowance
          ? allowanceRows
          : <PositionWithTaxCategorySumRow label={lang.taxCategoryPositions.totalNet} value={formatAmount(totalNet)} />}
        { vatComponents }
        <PositionWithTaxCategorySumRow label={lang.taxCategoryPositions.total} value={formatAmount(totalGross)} bold />
      </>)
    : (
      <>
        { allowanceRows }
        <PositionWithTaxCategorySumRow label={lang.taxCategoryPositions.total} value={formatAmount(totalGross)} bold />
        { vatComponents }
      </>)

  // FLAG:useDocuAi — the review block: show what the DOCUMENT says (editable), and compare it
  // against what the positions add up to. Two independent checks, because they fail for different
  // reasons: the positions not matching the stated net points at a misread line, while net + VAT
  // not matching the stated total points at a misread total (or a discount nobody typed in).
  const documentTotalsBlock = useMemo(() => {
    if (!documentTotals) { return null }
    const statedNet = Number(documentTotals.priceNet ?? 0)
    const statedVat = Number(documentTotals.vatAmount ?? 0)
    const statedTotal = Number(documentTotals.totalAmount ?? 0)
    // Subtract the allowance only when the pair is complete (`hasAllowance`, the same gate the
    // discount row uses): an allowance WITHOUT positionsTotal means the lines already contain
    // the discount as its own negative row (UPS summary table), and subtracting it again showed
    // −2,332.10 € as "line-item total" for a €489.80 invoice. Legacy callers never pass either
    // prop, so allowance is 0 there and this stays value-identical.
    const positionsNet = new Decimal(totalNet || 0).minus(hasAllowance ? allowance : 0).toDP(2).toNumber()
    const netPlusVat = new Decimal(statedNet).plus(statedVat).toDP(2).toNumber()
    // FLAG:useDocuAi — EXACT comparison (plan omxweb-draft-receipt-instead-of-review-row T20,
    // 2026-09-18). Until then a cent of tolerance hid the one case this block exists for: since
    // T16 the booked expense shows the document's totals, and after a legacy save had drifted the
    // total by a cent the form read 1.320,45 + 92,43 next to a stated 1.412,89 — and said nothing.
    // Both figures of every check are on screen, so a difference the user can see is a difference
    // the hint names, in the wording it always had. Per-line rounding is what the VAT hint below
    // is FOR, not a reason to hide it (Ralf, 2026-09-18: the hints were always there before).
    const differs = (a, b) => !new Decimal(a || 0).toDP(2).eq(new Decimal(b || 0).toDP(2))
    const hints = []
    // FLAG:useDocuAi — the document states several rates and prints none per position, so the
    // positions-vs-net comparison would measure us and not the document — see assumedLineTaxHint.
    // Replace it (not merely hide it) with the hint that says what actually happened:
    //   * no line rated at all -> the printed tax groups ARE the positions now, and the reviewer
    //     needs to know why the article lines are not on screen;
    //   * some lines rated -> the rest were derived at the default rate and need assigning.
    // The net+VAT check below is unaffected either way: both of its figures come off the document.
    const lineTaxAssumed = hasAssumedLineTax(assumedLineTax)
    if (lineTaxAssumed) {
      const rates = formatStatedRates(assumedLineTax)
      hints.push(isBookedByTaxRate(assumedLineTax)
        ? lang.taxCategoryPositions.totalsMismatch.lineTaxByRate(rates)
        : lang.taxCategoryPositions.totalsMismatch.lineTaxUnknown(rates))
    } else if (statedNet && differs(positionsNet, statedNet)) {
      hints.push(lang.taxCategoryPositions.totalsMismatch.positions(formatAmount(positionsNet), formatAmount(statedNet)))
    }
    if (statedTotal && differs(netPlusVat, statedTotal)) {
      hints.push(lang.taxCategoryPositions.totalsMismatch.total(formatAmount(netPlusVat), formatAmount(statedTotal)))
    }
    // FLAG:useDocuAi — third check: the POSITIONS' tax against the tax the document states.
    // The two checks above only ever compare printed figures with printed figures, so they
    // both stay silent while the three stated totals agree — even when the lines next to
    // them claim a completely different tax. Issue 7bf92ff5 (insurance premium invoice,
    // "Versicherungsbeiträge sind umsatzsteuerfrei"): the header read
    // 599,73 = 599,73 + 0,00 and was internally consistent, while both positions carried
    // 19 % and 24,68 € / 89,27 € — 113,95 € of VAT the document never charges, on screen
    // without a single hint. Pass it through if we must, but say so.
    //
    // Two deliberate silences, each because the difference would then be legitimate and
    // the hint would measure us instead of the document:
    //   * `lineTaxAssumed` — the per-line rate is our assumption (see assumedLineTaxHint);
    //   * `documentVat`/`hasAllowance` — a whole-invoice allowance lowers the taxed base,
    //     so Σ line VAT is SUPPOSED to exceed the VAT the document states (the same reason
    //     `documentVat` overrides the summed map above).
    // No line-count tolerance either (T20): `calculatePosition` rounds VAT per line, so a multi-line
    // gross receipt drifts a cent or two against the stated VAT — the booked value is the document's,
    // and that drift is exactly what the reviewer asked to be told about.
    const positionsVat = positions.reduce(
      (sum, position) => sum.plus(position.vat || 0), new Decimal(0),
    ).toDP(2).toNumber()
    if (
      positions.length
      && !lineTaxAssumed
      && !documentVat
      && !hasAllowance
      && differs(positionsVat, statedVat)
    ) {
      hints.push(lang.taxCategoryPositions.totalsMismatch.vat(
        formatAmount(positionsVat), formatAmount(statedVat),
      ))
      // T21: name the CAUSE while the gap is one per-line rounding step per line — the range that
      // was this check's silence until T20. `calculatePosition` divides and rounds each line on its
      // own, so Σ round(…) legitimately misses round(Σ …) by a cent or two, and the reviewer needs
      // to know that before hunting for a typo. Beyond that range it is NOT rounding (the insurance
      // receipt states 0,00 € against 113,95 € of line VAT), and explaining it away would be a lie.
      // Decimal, not float: `Math.abs(1.19 - 1.18)` is 1.0000000000000009e-2 and therefore ABOVE
      // 0,01 — the one-line, one-cent case would lose the very sentence it exists for (measured
      // 2026-09-18 in the omxweb review of 3ddcf86a34). The decision two lines up was moved to
      // Decimal for the same reason; this one has to follow.
      if (new Decimal(positionsVat).minus(statedVat).abs()
        .lte(new Decimal('0.01').times(positions.length))) {
        hints.push(lang.taxCategoryPositions.totalsMismatch.vatRoundingCause)
      }
    }
    // FLAG:useDocuAi — VAT per rate as stated by the RECEIPT (EN 16931 BG-23; data.vat from the
    // analysis, passed as documentVat by receipt-form only at the XML gate). It MUST live in THIS
    // block: the component renders `documentTotalsBlock || positionSum`, and documentVat never
    // exists without documentTotals — a breakdown in the positionSum branch would be unreachable in
    // the application. Nothing is calculated here (the payload map is authoritative): a document
    // allowance reduces the taxable base, so Σ line VAT legitimately lies above it — and was locked
    // on XML, making it impossible to correct. Without documentVat, the block renders as before.
    const statedVatRows = documentVat
      ? Object.entries(documentVat)
        .map(([rate, statedAmount]) => [rate, Number(statedAmount) || 0])
        .filter(([, statedAmount]) => statedAmount !== 0)
        .sort(([rateA], [rateB]) => Number(rateB) - Number(rateA))
        .map(([rate, statedAmount]) => (
          <PositionWithTaxCategorySumRow
            key={`document-vat-${rate}`}
            label={lang.taxCategoryPositions.documentVatRate(rate)}
            value={formatAmount(statedAmount)} />
        ))
      : null
    const amount = (field, value, ariaLabel) => (
      <InlineAmountEdit
        dataQsId={`positionsWithTaxCategorySum-${field}`}
        value={value}
        ariaLabel={ariaLabel}
        readOnly={disabled || lockDocumentTotals || !onChangeDocumentTotal}
        title={lockDocumentTotals ? lang.documents.xmlLockedValueHint : undefined}
        currencySymbol={currencySymbol}
        onChange={next => onChangeDocumentTotal(field, next)}
      />
    )
    return (
      <>
        <PositionWithTaxCategorySumRow
          label={lang.taxCategoryPositions.positionsTotal}
          value={formatAmount(positionsNet)} />
        <PositionWithTaxCategorySumRow
          label={lang.taxCategoryPositions.documentNet}
          value={amount('priceNet', statedNet, lang.taxCategoryPositions.documentNet)} />
        <PositionWithTaxCategorySumRow
          label={lang.taxCategoryPositions.documentVat}
          value={amount('vatAmount', statedVat, lang.taxCategoryPositions.documentVat)} />
        {statedVatRows}
        <PositionWithTaxCategorySumRow
          label={lang.taxCategoryPositions.documentTotal}
          value={amount('totalAmount', statedTotal, lang.taxCategoryPositions.documentTotal)}
          bold />
        {!!hints.length && (
          <li className={classes.mismatch} data-qs-id='positionsWithTaxCategorySum-mismatch'>
            {hints.map(hint => <div key={hint}>{hint}</div>)}
          </li>
        )}
      </>
    )
  }, [documentTotals, totalNet, positions, documentVat, allowance, hasAllowance, assumedLineTax, disabled, lockDocumentTotals, onChangeDocumentTotal, classes.mismatch])

  const addPosition = hideAddPosition
    ? null
    : (
      <div className={classes.addButton} onClick={() => !disabled && handleAddPosition()}>
        <div className={classes.addButtonIconWrapper}>
          <SVGIcon
            svg={AddIconSvg}
            width='12px'
            height='12px'
            fill='#ffffff' />
        </div>
        <span className={classes.addButtonLabel}>{lang.taxCategoryPositions.newPosition}</span>
      </div>
    )

  return (
    <div className={clsx(classes.container, hideAddPosition && classes.containerReverse)}>
      {addPosition}
      <div className={classes.positionSumRow}>
        {!hidePriceKindSwitch && (
          <div className={classes.switchWrapper}>
            <PriceKindSwitch
              disabled={disabled}
              priceKind={priceKind}
              onChangePriceKind={onChangePriceKind}
            />
          </div>
        )}
        {documentTotalsBlock || positionSum}
      </div>
    </div>
  )
}

export default PositionsWithTaxCategorySum
